import React, {
  useState, useEffect, useCallback, useMemo, useRef
} from "react";
import { API_BASE } from "../api/config";
import "./FahrtenbuchPage.css";

/* ── API ──────────────────────────────────────────────────────────── */
const api = (path, opts = {}) =>
  fetch(API_BASE + "/fahrtenbuch" + path, {
    headers: { "Content-Type": "application/json", ...opts.headers },
    ...opts,
  }).then(async (r) => {
    if (!r.ok) {
      let d = "";
      try { const j = await r.json(); d = j.detail || ""; } catch {}
      throw new Error("API " + r.status + (d ? ": " + d : ""));
    }
    return r.json();
  });

const STATUS_LABEL = {
  auto:         { label: "✦ Auto",         cls: "auto"         },
  kalender:     { label: "◈ Kalender",     cls: "kalender"     },
  manuell:      { label: "✎ Manuell",      cls: "manuell"      },
  unzugeordnet: { label: "⚠ Unzugeordnet", cls: "unzugeordnet" },
};

const MONTHS_DE = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember"
];
const WEEKDAYS = ["Mo","Di","Mi","Do","Fr","Sa","So"];

/* ── Haupt-Komponente ──────────────────────────────────────────────── */
export default function FahrtenbuchPage() {
  const today = new Date();
  const [view, setView]               = useState("kalender"); // "kalender" | "liste"
  const [year, setYear]               = useState(today.getFullYear());
  const [month, setMonth]             = useState(today.getMonth()); // 0-basiert
  const [fahrten, setFahrten]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [modal, setModal]             = useState(null); // { mode: "new"|"edit"|"view", fahrt? }
  const [toast, setToast]             = useState(null);
  const [selectedObjekte, setSelectedObjekte] = useState(null); // null = alle
  const [excelModal, setExcelModal]           = useState(false);

  /* Daten laden */
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await api(`/?jahr=${year}&limit=1000`);
      setFahrten(data.fahrten || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  /* Toast */
  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const [polling, setPolling]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const icsInputRef               = useRef(null);

  /* ICS-Datei hochladen */
  const uploadIcs = async (e) => {
    const file = e.target.files?.[0];
    if (!icsInputRef.current) return;
    icsInputRef.current.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch(API_BASE + "/fahrtenbuch/ical/upload", { method: "POST", body: form });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || r.status); }
      const data = await r.json();
      if (data.inserted > 0) {
        showToast(`${data.inserted} Termin${data.inserted > 1 ? "e" : ""} importiert ✓`);
        load();
      } else if (data.errors?.length) {
        showToast(data.errors[0], "err");
      } else {
        showToast(`Keine neuen Termine in der Datei (${data.total} Ereignisse, ${data.skipped} übersprungen)`);
      }
    } catch (e) { showToast(e.message, "err"); }
    finally { setUploading(false); }
  };

  /* Mail-Poll auslösen */
  const pollMail = async () => {
    setPolling(true);
    try {
      const data = await api("/mail/poll?all=true", { method: "POST" });
      if (data.inserted > 0) {
        showToast(`${data.inserted} neuer Termin importiert ✓`);
        load();
      } else if (data.errors?.length) {
        showToast(data.errors[0], "err");
      } else {
        showToast(`Keine neuen Kalender-Einladungen (${data.processed} E-Mails geprüft)`);
      }
    } catch (e) { showToast(e.message, "err"); }
    finally { setPolling(false); }
  };


  const [unzugeordnet, setUnzugeordnet]   = useState([]);
  const [uzLoading, setUzLoading]         = useState(false);
  const [zuordnenModal, setZuordnenModal] = useState(null); // fahrt-Objekt

  const loadUnzugeordnet = useCallback(async () => {
    setUzLoading(true);
    try {
      const data = await api("/unzugeordnet/");
      setUnzugeordnet(data.fahrten || []);
    } catch {}
    finally { setUzLoading(false); }
  }, []);

  useEffect(() => { loadUnzugeordnet(); }, [loadUnzugeordnet]);

  const deleteFahrt = async (id) => {
    if (!window.confirm("Eintrag löschen?")) return;
    try {
      await api(`/${id}`, { method: "DELETE" });
      showToast("Eintrag gelöscht");
      load();
    } catch (e) { showToast(e.message, "err"); }
  };

  /* ── Objekt-Filter ─────────────────────────────────────────────── */
  const allObjekte = useMemo(() => {
    const set = new Set(fahrten.map(f => f.objekt_kuerzel).filter(Boolean));
    return [...set].sort();
  }, [fahrten]);

  const toggleObjekt = (k) => {
    setSelectedObjekte(prev => {
      const next = new Set(prev ?? allObjekte);
      if (next.has(k)) { next.delete(k); } else { next.add(k); }
      if (next.size === allObjekte.length) return null; // alle = null
      if (next.size === 0) return new Set(); // keins
      return next;
    });
  };

  const selectAllObjekte  = () => setSelectedObjekte(null);
  const deselectAllObjekte = () => setSelectedObjekte(new Set());

  const filteredFahrten = useMemo(() =>
    selectedObjekte == null
      ? fahrten
      : fahrten.filter(f => selectedObjekte.has(f.objekt_kuerzel)),
  [fahrten, selectedObjekte]);

  /* ── Kalender-Helfer ──────────────────────────────────────────────── */
  const fahrtenByDate = useMemo(() => {
    const m = {};
    for (const f of filteredFahrten) {
      const d = f.fahrt_datum?.slice(0, 10);
      if (!d) continue;
      (m[d] = m[d] || []).push(f);
    }
    return m;
  }, [filteredFahrten]);

  const calendarWeeks = useMemo(() => {
    // Erster Tag des Monats
    const first = new Date(year, month, 1);
    // ISO-Wochenstartoffset: 0=Mo … 6=So
    let startDow = (first.getDay() + 6) % 7; // 0=Mo
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    // Auf vollen 7er-Block auffüllen
    while (cells.length % 7) cells.push(null);

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7)
      weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, [year, month]);

  /* Fahrten für aktuellen Monat im Kalender */
  const monthPrefix = `${year}-${String(month + 1).padStart(2, "0")}-`;

  /* ── Monats-Navigation ─────────────────────────────────────────── */
  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <div className="fb-page">
      {/* Header */}
      <div className="fb-header">
        <div className="fb-title">
          <h1>Fahrtenbuch</h1>
        </div>
        <div className="fb-header-actions">
          <div className="fb-view-toggle">
            <button
              className={view === "kalender" ? "active" : ""}
              onClick={() => setView("kalender")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              Kalender
            </button>
            <button
              className={view === "liste" ? "active" : ""}
              onClick={() => setView("liste")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              Liste
            </button>
          </div>
          <button className="fb-btn-primary" onClick={() => setModal({ mode: "new" })}>
            + Eintrag
          </button>
          <button
            className="fb-btn-secondary"
            onClick={pollMail}
            disabled={polling}
            title="Apple Calendar Einladungen abrufen"
          >
            {polling ? "…" : "Poll"}
          </button>
          <button
            className="fb-btn-secondary"
            onClick={() => icsInputRef.current?.click()}
            disabled={uploading}
            title="ICS-Datei hochladen"
          >
            {uploading ? "…" : "ICS"}
          </button>
          <input
            ref={icsInputRef}
            type="file"
            accept=".ics,text/calendar"
            style={{ display: "none" }}
            onChange={uploadIcs}
          />
          <button className="fb-btn-secondary" onClick={() => setExcelModal(true)}>
            ↓ Excel
          </button>
        </div>
      </div>

      {error && <div className="fb-error">{error}</div>}

      {/* Jahres-Wahl */}
      <div className="fb-year-bar">
        <button className="fb-nav-btn" onClick={() => setYear(y => y - 1)}>‹</button>
        <span className="fb-year-label">{year}</span>
        <button className="fb-nav-btn" onClick={() => setYear(y => y + 1)}>›</button>
        <span className="fb-stats">
          {filteredFahrten.length} Fahrten ·{" "}
          {filteredFahrten.reduce((s, f) => s + (f.km_pauschale || 0), 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
        </span>
      </div>

      {/* Objekt-Filter */}
      {allObjekte.length > 1 && (
        <div className="fb-filter-bar">
          <button
            className={`fb-filter-chip all ${selectedObjekte == null ? "active" : ""}`}
            onClick={selectAllObjekte}
            title="Alle Objekte anzeigen"
          >Alle</button>
          <button
            className="fb-filter-chip ctrl"
            onClick={deselectAllObjekte}
            title="Alle ausblenden"
          >Keines</button>
          <span className="fb-filter-sep" />
          {allObjekte.map(k => {
            const active = selectedObjekte == null || selectedObjekte.has(k);
            return (
              <button
                key={k}
                className={`fb-filter-chip ${active ? "active" : ""}`}
                onClick={() => toggleObjekt(k)}
              >{k}</button>
            );
          })}
          {selectedObjekte != null && (
            <span className="fb-filter-count">
              {selectedObjekte.size} / {allObjekte.length} ausgewählt
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="fb-loading">Lade Daten…</div>
      ) : view === "kalender" ? (
        <CalendarView
          year={year} month={month}
          calendarWeeks={calendarWeeks}
          monthPrefix={monthPrefix}
          fahrtenByDate={fahrtenByDate}
          prevMonth={prevMonth} nextMonth={nextMonth}
          onAdd={(date) => setModal({ mode: "new", prefillDate: date })}
          onView={(f) => setModal({ mode: "view", fahrt: f })}
          onDelete={deleteFahrt}
        />
      ) : (
        <ListView
          fahrten={filteredFahrten}
          onEdit={(f) => setModal({ mode: "edit", fahrt: f })}
          onDelete={deleteFahrt}
        />
      )}

      {/* Unzugeordnete Fahrten */}
      {(uzLoading || unzugeordnet.length > 0) && (
        <UnzugeordnetSektion
          fahrten={unzugeordnet}
          loading={uzLoading}
          onZuordnen={(f) => setZuordnenModal(f)}
          onDelete={async (id) => {
            if (!window.confirm("Eintrag löschen?")) return;
            try { await api(`/${id}`, { method: "DELETE" }); loadUnzugeordnet(); showToast("Gelöscht"); }
            catch (e) { showToast(e.message, "err"); }
          }}
        />
      )}

      {/* Modal */}
      {modal && (
        <FahrtModal
          mode={modal.mode}
          fahrt={modal.fahrt}
          prefillDate={modal.prefillDate}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); showToast("Gespeichert ✓"); }}
          onError={(e) => showToast(e, "err")}
        />
      )}

      {/* Excel-Export-Modal */}
      {excelModal && (
        <ExcelExportModal
          allObjekte={allObjekte}
          currentYear={year}
          onClose={() => setExcelModal(false)}
        />
      )}

      {/* Zuordnen-Modal */}
      {zuordnenModal && (
        <ZuordnenModal
          fahrt={zuordnenModal}
          onClose={() => setZuordnenModal(null)}
          onSaved={() => {
            setZuordnenModal(null);
            loadUnzugeordnet();
            load();
            showToast("Zugeordnet ✓");
          }}
          onError={(e) => showToast(e, "err")}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fb-toast ${toast.type === "err" ? "err" : ""}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ── Kalender-Ansicht ──────────────────────────────────────────────── */
function CalendarView({
  year, month, calendarWeeks, monthPrefix,
  fahrtenByDate, prevMonth, nextMonth,
  onAdd, onView, onDelete,
}) {
  const today = new Date();
  const isToday = (d) =>
    today.getFullYear() === year &&
    today.getMonth() === month &&
    today.getDate() === d;

  return (
    <div className="fb-calendar">
      {/* Monats-Header */}
      <div className="fb-cal-header">
        <button className="fb-nav-btn" onClick={prevMonth}>‹</button>
        <span className="fb-cal-month">{MONTHS_DE[month]} {year}</span>
        <button className="fb-nav-btn" onClick={nextMonth}>›</button>
      </div>

      {/* Wochentage */}
      <div className="fb-cal-weekdays">
        {WEEKDAYS.map(d => <div key={d} className="fb-cal-wd">{d}</div>)}
      </div>

      {/* Wochen */}
      <div className="fb-cal-grid">
        {calendarWeeks.map((week, wi) =>
          week.map((day, di) => {
            const key = `${wi}-${di}`;
            if (!day) return <div key={key} className="fb-cal-cell empty" />;

            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const eintraege = fahrtenByDate[dateStr] || [];

            return (
              <div
                key={key}
                className={`fb-cal-cell ${isToday(day) ? "today" : ""} ${eintraege.length ? "has-entries" : ""}`}
                onClick={() => onAdd(dateStr)}
              >
                <span className="fb-cal-day">{day}</span>
                {eintraege.map(f => (
                  <div
                    key={f.id}
                    className={`fb-cal-dot ${STATUS_LABEL[f.fahrt_status]?.cls || ""}`}
                    title={`${f.objekt_kuerzel} · ${f.fahrtgrund}`}
                    onClick={(e) => { e.stopPropagation(); onView(f); }}
                  >
                    <span className="fb-cal-dot-label">{f.objekt_kuerzel} – {f.fahrtgrund}</span>
                    <button
                      className="fb-cal-del"
                      onClick={(e) => { e.stopPropagation(); onDelete(f.id); }}
                      title="Löschen"
                    >×</button>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>

      {/* Legende */}
      <div className="fb-legend">
        {Object.entries(STATUS_LABEL).map(([k, v]) => (
          <span key={k} className={`fb-legend-item ${v.cls}`}>{v.label}</span>
        ))}
      </div>
    </div>
  );
}

/* ── Listen-Ansicht ───────────────────────────────────────────────── */
function ListView({ fahrten, onEdit, onDelete }) {
  const fmt = (v) => v ? new Date(v).toLocaleDateString("de-DE") : "—";
  const fmtEur = (v) => v != null
    ? Number(v).toLocaleString("de-DE", { style: "currency", currency: "EUR" })
    : "—";

  return (
    <div className="fb-list">
      {fahrten.length === 0 ? (
        <div className="fb-empty">Keine Einträge für dieses Jahr.</div>
      ) : (
        <table className="fb-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Objekt</th>
              <th>Person</th>
              <th>Fahrtgrund</th>
              <th>Detail</th>
              <th>km (einfach)</th>
              <th>km gesamt</th>
              <th>Pauschale</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {fahrten.map(f => (
              <tr key={f.id}>
                <td className="fb-date">{fmt(f.fahrt_datum)}</td>
                <td><span className="fb-objekt">{f.objekt_kuerzel}</span></td>
                <td>{f.person_kuerzel ? <span className="fb-person-tag">{f.person_kuerzel}</span> : <span className="fb-date">—</span>}</td>
                <td>{f.fahrtgrund}</td>
                <td className="fb-detail">{f.detail_begruendung || "—"}</td>
                <td className="fb-num">{f.entfernung_km ? `${Number(f.entfernung_km).toFixed(0)} km` : "—"}</td>
                <td className="fb-num">{f.km_gesamt ? `${Number(f.km_gesamt).toFixed(0)} km` : "—"}</td>
                <td className="fb-num fb-kp">{fmtEur(f.km_pauschale)}</td>
                <td>
                  <span className={`fb-status-badge ${STATUS_LABEL[f.fahrt_status]?.cls || ""}`}>
                    {STATUS_LABEL[f.fahrt_status]?.label || f.fahrt_status}
                  </span>
                </td>
                <td className="fb-actions">
                  {f.fahrt_status !== "auto" && f.fahrt_status !== "kalender" && (
                    <button className="fb-btn-icon" onClick={() => onEdit(f)} title="Bearbeiten">✎</button>
                  )}
                  <button className="fb-btn-icon danger" onClick={() => onDelete(f.id)} title="Löschen">×</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="7" className="fb-sum-label">Summe Kilometerpauschale:</td>
              <td className="fb-num fb-kp fb-sum">
                {fahrten.reduce((s, f) => s + (Number(f.km_pauschale) || 0), 0)
                  .toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </td>
              <td colSpan="2" />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

/* ── Modal: Neu / Bearbeiten / Anzeigen ───────────────────────────── */
function FahrtModal({ mode, fahrt, prefillDate, onClose, onSaved, onError }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    objekt_kuerzel:     fahrt?.objekt_kuerzel     || "084",
    fahrt_datum:        fahrt?.fahrt_datum?.slice(0,10) || prefillDate || today,
    fahrtgrund:         fahrt?.fahrtgrund          || "",
    detail_begruendung: fahrt?.detail_begruendung  || "",
    referenz:           fahrt?.referenz            || "",
    entfernung_km:      fahrt?.entfernung_km       != null ? String(fahrt.entfernung_km) : "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.fahrtgrund.trim()) { onError("Fahrtgrund fehlt"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        entfernung_km: form.entfernung_km ? parseFloat(form.entfernung_km) : null,
      };
      if (mode === "edit" && fahrt) {
        await api(`/${fahrt.id}`, { method: "PUT", body: JSON.stringify(body) });
      } else {
        await api("/", { method: "POST", body: JSON.stringify(body) });
      }
      onSaved();
    } catch (e) { onError(e.message); setSaving(false); }
  };

  const readOnly = mode === "view";
  const title = mode === "new" ? "Neue Fahrt" : mode === "edit" ? "Fahrt bearbeiten" : "Fahrt ansehen";

  return (
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()}>
        <div className="fb-modal-header">
          <h2>{title}</h2>
          <button className="fb-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="fb-modal-body">
          <div className="fb-form-row">
            <label>Objekt</label>
            <input value={form.objekt_kuerzel} onChange={set("objekt_kuerzel")} readOnly={readOnly} />
          </div>
          <div className="fb-form-row">
            <label>Datum</label>
            <input type="date" value={form.fahrt_datum} onChange={set("fahrt_datum")} readOnly={readOnly} />
          </div>
          <div className="fb-form-row">
            <label>Fahrtgrund</label>
            <input value={form.fahrtgrund} onChange={set("fahrtgrund")} readOnly={readOnly}
              placeholder="z.B. Handwerkerbesuch" />
          </div>
          <div className="fb-form-row">
            <label>Detail</label>
            <input value={form.detail_begruendung} onChange={set("detail_begruendung")} readOnly={readOnly}
              placeholder="Optionale Beschreibung" />
          </div>
          <div className="fb-form-row">
            <label>Referenz</label>
            <input value={form.referenz} onChange={set("referenz")} readOnly={readOnly}
              placeholder="Dateiname / Rechnungsnummer" />
          </div>
          <div className="fb-form-row">
            <label>Entfernung (einfach km)</label>
            <input type="number" value={form.entfernung_km} onChange={set("entfernung_km")} readOnly={readOnly}
              placeholder="110 (aus Config wenn leer)" />
          </div>

          {mode === "view" && fahrt && (
            <div className="fb-view-extra">
              <div className="fb-form-row"><label>km gesamt</label><span>{fahrt.km_gesamt ? `${Number(fahrt.km_gesamt).toFixed(0)} km` : "—"}</span></div>
              <div className="fb-form-row"><label>Pauschale</label><span>{fahrt.km_pauschale ? Number(fahrt.km_pauschale).toLocaleString("de-DE", { style: "currency", currency: "EUR" }) : "—"}</span></div>
              <div className="fb-form-row"><label>Status</label>
                <span className={`fb-status-badge ${STATUS_LABEL[fahrt.fahrt_status]?.cls || ""}`}>
                  {STATUS_LABEL[fahrt.fahrt_status]?.label || fahrt.fahrt_status}
                </span>
              </div>
              {fahrt.absender_id && <div className="fb-form-row"><label>Absender-ID</label><span>{fahrt.absender_id}</span></div>}
            </div>
          )}
        </div>
        {!readOnly && (
          <div className="fb-modal-footer">
            <button className="fb-btn-secondary" onClick={onClose} disabled={saving}>Abbrechen</button>
            <button className="fb-btn-primary" onClick={save} disabled={saving}>
              {saving ? "Speichern…" : "Speichern"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Excel-Export-Modal ───────────────────────────────────────────── */
function ExcelExportModal({ allObjekte, currentYear, onClose }) {
  const thisYear = currentYear || new Date().getFullYear();
  const years = [];
  for (let y = 2020; y <= thisYear + 1; y++) years.push(y);

  const [objekt,   setObjekt]   = useState("");
  const [zeitraum, setZeitraum] = useState("jahr");
  const [jahr,     setJahr]     = useState(thisYear);
  const [monat,    setMonat]    = useState(new Date().getMonth() + 1);

  const download = () => {
    const params = new URLSearchParams();
    if (objekt)                                        params.set("objekt", objekt);
    if (zeitraum === "jahr" || zeitraum === "monat")   params.set("jahr",   jahr);
    if (zeitraum === "monat")                          params.set("monat",  monat);
    window.open(`${API_BASE}/fahrtenbuch/export/excel?${params.toString()}`, "_blank");
    onClose();
  };

  const previewName = () => {
    const o = objekt || "Alle Objekte";
    if (zeitraum === "alle")  return `Fahrtenbuch – ${o} – Gesamt`;
    if (zeitraum === "jahr")  return `Fahrtenbuch – ${o} – ${jahr}`;
    return `Fahrtenbuch – ${o} – ${MONTHS_DE[monat - 1]} ${jahr}`;
  };

  return (
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal fb-modal-export" onClick={e => e.stopPropagation()}>
        <div className="fb-modal-header">
          <h2>Excel herunterladen</h2>
          <button className="fb-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="fb-modal-body">

          <div className="fb-form-row">
            <label>Objekt</label>
            <select value={objekt} onChange={e => setObjekt(e.target.value)}>
              <option value="">Alle Objekte</option>
              {allObjekte.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>

          <div className="fb-form-row">
            <label>Zeitraum</label>
            <div className="fb-radio-group">
              {[
                { v: "alle",  l: "Gesamter Zeitraum" },
                { v: "jahr",  l: "Ganzes Jahr" },
                { v: "monat", l: "Einzelner Monat" },
              ].map(({ v, l }) => (
                <label key={v} className="fb-radio-label">
                  <input type="radio" name="zeitraum" value={v}
                    checked={zeitraum === v} onChange={() => setZeitraum(v)} />
                  {l}
                </label>
              ))}
            </div>
          </div>

          {(zeitraum === "jahr" || zeitraum === "monat") && (
            <div className="fb-form-row">
              <label>Jahr</label>
              <select value={jahr} onChange={e => setJahr(Number(e.target.value))}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}

          {zeitraum === "monat" && (
            <div className="fb-form-row">
              <label>Monat</label>
              <select value={monat} onChange={e => setMonat(Number(e.target.value))}>
                {MONTHS_DE.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
          )}

          <div className="fb-export-preview">
            <span>📄 {previewName()}.xlsx</span>
          </div>
        </div>
        <div className="fb-modal-footer">
          <button className="fb-btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="fb-btn-primary" onClick={download}>↓ Herunterladen</button>
        </div>
      </div>
    </div>
  );
}

/* ── Unzugeordnete Fahrten ─────────────────────────────────────────── */
function UnzugeordnetSektion({ fahrten, loading, onZuordnen, onDelete }) {
  const fmt = (v) => v ? new Date(v).toLocaleDateString("de-DE") : "—";
  return (
    <div className="fb-unzugeordnet">
      <div className="fb-uz-header">
        <span className="fb-uz-icon">⚠</span>
        <h3>Nicht zugeordnet ({fahrten.length})</h3>
        <span className="fb-uz-hint">Diese Einträge konnten keinem Objekt zugeordnet werden</span>
      </div>
      {loading ? <div className="fb-loading">…</div> : (
        <table className="fb-table">
          <thead><tr>
            <th>Datum</th><th>Kalender-Betreff</th><th>Person</th><th>Fahrtgrund</th><th></th>
          </tr></thead>
          <tbody>
            {fahrten.map(f => (
              <tr key={f.id} className="fb-uz-row">
                <td>{fmt(f.fahrt_datum)}</td>
                <td className="fb-uz-summary">{f.ical_summary || f.email_betreff || "—"}</td>
                <td>{f.person_kuerzel || "—"}</td>
                <td>{f.fahrtgrund}</td>
                <td className="fb-actions">
                  <button className="fb-btn-primary fb-btn-sm" onClick={() => onZuordnen(f)}>Zuordnen</button>
                  <button className="fb-btn-icon danger" onClick={() => onDelete(f.id)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ── Zuordnen Modal ────────────────────────────────────────────────── */
function ZuordnenModal({ fahrt, onClose, onSaved, onError }) {
  const [objekt, setObjekt] = useState(fahrt.person_kuerzel || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!objekt.trim()) { onError("Objekt/Kürzel fehlt"); return; }
    setSaving(true);
    try {
      await api(`/${fahrt.id}/zuordnen`, {
        method: "POST",
        body: JSON.stringify({ objekt_kuerzel: objekt.trim() }),
      });
      onSaved();
    } catch (e) {
      onError(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()}>
        <div className="fb-modal-header">
          <h2>Fahrt zuordnen</h2>
          <button className="fb-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="fb-modal-body">
          <div className="fb-uz-info">
            <strong>{fahrt.ical_summary || fahrt.email_betreff}</strong>
            <span>{fahrt.fahrt_datum?.slice(0, 10)}</span>
          </div>
          <div className="fb-form-row">
            <label>Objekt / Kürzel</label>
            <input
              value={objekt}
              onChange={e => setObjekt(e.target.value.toUpperCase())}
              placeholder="z.B. 084 oder HIG oder 176-05"
              autoFocus
            />
          </div>
        </div>
        <div className="fb-modal-footer">
          <button className="fb-btn-secondary" onClick={onClose} disabled={saving}>Abbrechen</button>
          <button className="fb-btn-primary" onClick={save} disabled={saving}>
            {saving ? "…" : "Zuordnen"}
          </button>
        </div>
      </div>
    </div>
  );
}
